/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

// Configured leave type canonical keys
export const LEAVE_TYPE_CONGE_PAYE = 'conge_paye';
export const LEAVE_TYPE_CONGE_ANCIENNETE = 'conge_anciennete';
export const LEAVE_TYPE_ABSENCE_NON_REMUNEREE = 'absence_non_remuneree';
export const LEAVE_TYPE_EVENEMENT_DECES = 'evenement_deces';

// Mirrors BereavementLeaveService::RELATIONSHIPS.
export const FAMILY_RELATIONSHIPS = [
  {id: 'spouse', label: 'Conjoint'},
  {id: 'child', label: 'Enfant'},
  {id: 'brother', label: 'Frère'},
  {id: 'sister', label: 'Sœur'},
  {id: 'mother', label: 'Mère'},
  {id: 'father', label: 'Père'},
  {id: 'parent_in_law', label: 'Beau-parent'},
  {id: 'grandparent', label: 'Grand-parent'},
  {id: 'other', label: 'Autre'},
];

// Leave type display names by canonical key — mirrors LeaveTypeConfigService::LEAVE_TYPE_NAMES_BY_KEY
export const LEAVE_TYPE_NAMES_BY_KEY = {
  [LEAVE_TYPE_CONGE_PAYE]: 'Congé Payé',
  [LEAVE_TYPE_CONGE_ANCIENNETE]: 'Congé Ancienneté',
  [LEAVE_TYPE_ABSENCE_NON_REMUNEREE]: 'Absence non rémunérée',
  [LEAVE_TYPE_EVENEMENT_DECES]: 'Événement familial Décès',
};

export function isBereavementLeave(name: string): boolean {
  return name === LEAVE_TYPE_NAMES_BY_KEY[LEAVE_TYPE_EVENEMENT_DECES];
}

/**
 * Check if a leave type name matches the configured unpaid leave type.
 * @param leaveTypeName The display name of the selected leave type
 * @returns true if the leave type is the configured unpaid leave
 */
export function isUnpaidLeave(leaveTypeName: string): boolean {
  return (
    leaveTypeName === LEAVE_TYPE_NAMES_BY_KEY[LEAVE_TYPE_ABSENCE_NON_REMUNEREE]
  );
}

/**
 * Check if a leave type name matches the configured paid leave type (Congé Payé).
 * @param leaveTypeName The display name of the selected leave type
 * @returns true if the leave type is the configured paid leave
 */
export function isPaidLeave(leaveTypeName: string): boolean {
  return leaveTypeName === LEAVE_TYPE_NAMES_BY_KEY[LEAVE_TYPE_CONGE_PAYE];
}

/**
 * Check if a leave type name matches the configured seniority leave type (Congé Ancienneté).
 * @param leaveTypeName The display name of the selected leave type
 * @returns true if the leave type is the configured seniority leave
 */
export function isSeniorityLeave(leaveTypeName: string): boolean {
  return leaveTypeName === LEAVE_TYPE_NAMES_BY_KEY[LEAVE_TYPE_CONGE_ANCIENNETE];
}

/**
 * Check if a leave type is restricted to full-day duration only.
 * @param leaveTypeName The display name of the selected leave type
 * @returns true if the leave type permits only full-day duration
 */
export function isFullDayOnlyLeave(leaveTypeName: string): boolean {
  return isPaidLeave(leaveTypeName) || isSeniorityLeave(leaveTypeName);
}
